import { defineStore } from "pinia";
import { ref } from "vue";
import { useAxios } from "@/composables/api.js";
import { setToken, removeToken } from "@/composables/token.js";
import { useAssessmentStore } from "@/stores/assessment";
import { catchError } from "@/composables/error.js";
import { Campaign } from "@/types/campaign";
import { User } from "@/types/user";
import { api } from "@/api/client";
import { fetchTokenQuery } from "@/queries/user.query";
import { queryClient } from "@/queries/queryClient";

export interface Invitation {
  campaign: Campaign,
  invitation_id: string,
  auto_activate: boolean,
  email: string,
  first_name: string,
  last_name: string,
  already_has_account: boolean,
}

export const useCandidateStore = defineStore("candidate", () => {
  const assessmentStore = useAssessmentStore();

  const invitation: Ref<Partial<Invitation>> = ref({});
  const candidate: Ref<User> = ref(null);
  const auth_token_game = ref(false);
  const joinInformations = ref({
    email: null,
    first_name: null,
    last_name: null,
    password: null,
    phone_number: {
      formatted: null,
    },
  });

  async function getAccount() {
    try {
      await fetchTokenQuery();
      const { data } = await axiosInstance.get("/api/v1/accounts");
      candidate.value = data;
    } catch (error) {
      candidate.value = null;
      console.error(error);
    }
  }


  function clear() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem("accountId");
    localStorage.removeItem("redirectUrl");
    queryClient.invalidateQueries({ queryKey: ["app-token"] });
    candidate.value = null;
  }

  function logout() {
    const clearFuncs = [assessmentStore.clear, clear];

    clearFuncs.forEach((fn) => fn());

    this.$router.push({ name: "auth" });
  }

  async function getSimplifiedReport(participantId) {
    try {
      const { data } = await useAxios.get(`/api/v1/participations/${participantId}/pdf/talent-simplified-report`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async function getFullReport(participantId) {
    try {
      const { data } = await useAxios.get(`/api/v1/participations/${participantId}/pdf/talent-full-report`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  return {
    invitation,
    candidate,
    auth_token_game,
    joinInformations,
    getAccount,
    logout,
    getSimplifiedReport,
    getFullReport
  };
});
